import { useEffect, useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Preferences {
  report_viewed_bell: boolean;
  report_viewed_email_milestone: boolean;
  report_viewed_email_digest: boolean;
  seller_feedback_bell: boolean;
  seller_feedback_email: boolean;
  showing_scheduled_bell: boolean;
  showing_scheduled_email: boolean;
  showing_feedback_bell: boolean;
  showing_feedback_email: boolean;
}

const DEFAULT_PREFS: Preferences = {
  report_viewed_bell: false,
  report_viewed_email_milestone: false,
  report_viewed_email_digest: false,
  seller_feedback_bell: false,
  seller_feedback_email: false,
  showing_scheduled_bell: false,
  showing_scheduled_email: false,
  showing_feedback_bell: false,
  showing_feedback_email: false,
};

function PrefRow({
  label,
  bellKey,
  emailKey,
  digestKey,
  prefs,
  onChange,
}: {
  label: string;
  bellKey: keyof Preferences;
  emailKey: keyof Preferences;
  digestKey?: keyof Preferences;
  prefs: Preferences;
  onChange: (key: keyof Preferences, value: boolean) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_64px_64px_64px] items-center gap-4 py-3 border-b border-border/50 last:border-b-0">
      <span className="text-sm text-foreground">{label}</span>
      <div className="flex justify-center">
        <Switch
          checked={prefs[bellKey]}
          onCheckedChange={(v) => onChange(bellKey, v)}
          aria-label={`${label} bell`}
        />
      </div>
      <div className="flex justify-center">
        <Switch
          checked={prefs[emailKey]}
          onCheckedChange={(v) => onChange(emailKey, v)}
          aria-label={`${label} email`}
        />
      </div>
      <div className="flex justify-center">
        {digestKey ? (
          <Switch
            checked={prefs[digestKey]}
            onCheckedChange={(v) => onChange(digestKey, v)}
            aria-label={`${label} digest`}
          />
        ) : (
          <span className="text-muted-foreground text-xs text-center">—</span>
        )}
      </div>
    </div>
  );
}

export function NotificationPreferences() {
  const [prefs, setPrefs] = useState<Preferences>(DEFAULT_PREFS);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPrefsRef = useRef<Preferences>(DEFAULT_PREFS);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      setUserId(session.user.id);

      const { data } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (data) {
        const loaded: Preferences = {
          report_viewed_bell: data.report_viewed_bell ?? false,
          report_viewed_email_milestone: data.report_viewed_email_milestone ?? false,
          report_viewed_email_digest: data.report_viewed_email_digest ?? false,
          seller_feedback_bell: data.seller_feedback_bell ?? false,
          seller_feedback_email: data.seller_feedback_email ?? false,
          showing_scheduled_bell: data.showing_scheduled_bell ?? false,
          showing_scheduled_email: data.showing_scheduled_email ?? false,
          showing_feedback_bell: data.showing_feedback_bell ?? false,
          showing_feedback_email: data.showing_feedback_email ?? false,
        };
        setPrefs(loaded);
        pendingPrefsRef.current = loaded;
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleChange = (key: keyof Preferences, value: boolean) => {
    const updated = { ...pendingPrefsRef.current, [key]: value };
    pendingPrefsRef.current = updated;
    setPrefs(updated);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!userId) return;
      const { error } = await supabase
        .from("notification_preferences")
        .upsert(
          { user_id: userId, ...pendingPrefsRef.current, updated_at: new Date().toISOString() },
          { onConflict: "user_id" }
        );
      if (error) {
        toast({ title: "Error", description: "Failed to save preferences", variant: "destructive" });
      }
    }, 500);
  };

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading preferences...</p>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>
            Choose how you want to be notified about activity on your reports. All notifications are off by default.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_64px_64px_64px] gap-4 pb-2 border-b border-border">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Event</span>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide text-center">Bell</span>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide text-center">Email</span>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide text-center">Digest</span>
          </div>

          <PrefRow
            label="Report viewed milestone (1, 5, 10, 25…)"
            bellKey="report_viewed_bell"
            emailKey="report_viewed_email_milestone"
            digestKey="report_viewed_email_digest"
            prefs={prefs}
            onChange={handleChange}
          />
          <PrefRow
            label="Seller price feedback on CMA"
            bellKey="seller_feedback_bell"
            emailKey="seller_feedback_email"
            prefs={prefs}
            onChange={handleChange}
          />
          <PrefRow
            label="Showing scheduled"
            bellKey="showing_scheduled_bell"
            emailKey="showing_scheduled_email"
            prefs={prefs}
            onChange={handleChange}
          />
          <PrefRow
            label="Showing feedback received"
            bellKey="showing_feedback_bell"
            emailKey="showing_feedback_email"
            prefs={prefs}
            onChange={handleChange}
          />

          <p className="text-xs text-muted-foreground mt-4">
            <strong>Bell</strong> — in-app notification badge.{" "}
            <strong>Email</strong> — instant email on each event.{" "}
            <strong>Digest</strong> — daily summary email each morning.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
